import * as React from "react";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
	useTheme,
} from "@material-ui/core/styles";
import {
	Grid,
	GridProps,
	SvgIconTypeMap,
	Typography,
	useMediaQuery,
} from "@material-ui/core";
import { OverridableComponent } from "@material-ui/core/OverridableComponent";

// Utility
import Utility from "../../domain/classes/Utility";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		detailsContainer: {
			display: "flex",
			alignContent: "center",
		},
		icon: { marginRight: theme.spacing(1) },
		textContainer: {
			display: "flex",
			alignItems: "center",
		},
	});

export interface AttributeContainerProps extends GridProps {
	Icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
	text?: string;
	number?: number;
	money?: number;
	minutes?: number;
	hasBracketsAroundMinutes?: boolean;
}

const AttributeContainer = React.forwardRef<
	HTMLDivElement,
	AttributeContainerProps & WithStyles<typeof styles>
>(
	(
		{
			classes,
			className,
			Icon,
			text,
			money,
			number,
			minutes,
			hasBracketsAroundMinutes,
			xs = 6,
			...gridProps
		},
		ref
	) => {
		// Theme
		const theme = useTheme();
		const matchXs = useMediaQuery(theme.breakpoints.down("xs"));

		return (
			<Grid
				item
				xs={xs}
				ref={ref}
				{...gridProps}
				className={clsx(classes.root, className)}
			>
				<div className={classes.detailsContainer}>
					<Icon
						fontSize={matchXs ? "small" : "default"}
						className={classes.icon}
					/>
					<div className={classes.textContainer}>
						<Typography variant={matchXs ? "caption" : "body2"}>
							{text && text}
							{money && `$${money.toFixed(2)}`}
							{number && number}
							{minutes && (
								<React.Fragment>
									{hasBracketsAroundMinutes
										? ` (${minutes} mins)`
										: `${minutes} mins`}
								</React.Fragment>
							)}
						</Typography>
					</div>
				</div>
			</Grid>
		);
	}
);

const StyledAttributeContainer = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("AttributeContainer"),
})(AttributeContainer);

export default StyledAttributeContainer;

export const AnimatedAttributeContainer = animated(StyledAttributeContainer);
